// InvestorWelcomeModal — first-run welcome wizard for the investor portal.
//
// Five short, icon-led screens (at most): what Veras does, the five steps
// on the Overview progress rail, how to reach us, Terms & Privacy acceptance,
// and an optional passkey setup. The legal and passkey steps are folded into
// the same flow instead of stacking separate dialogs on first sign-in.
//
//   - Terms & Privacy is only included while /api/auth/me reports
//     `needsTermsAcceptance`. It posts to the shared /api/accept-terms
//     endpoint (same as TermsAcceptanceGate) and the modal cannot be
//     dismissed until acceptance is recorded.
//   - Passkey runs the same register/start → register/finish ceremony as
//     PasskeyManager / PasskeySetupPrompt and is omitted when the device is
//     already handled. The device is marked handled only after Set up
//     succeeds or the investor clicks "Skip for now" on that step. Dismissing
//     earlier (Escape, outside click, Skip intro) leaves the standalone
//     PasskeySetupPrompt free to offer it.
//
// Brand treatment mirrors the eligibility questionnaire (PR #402): navy
// headings with tinted icon chips, gold accents via `rgba(var(--brand-gold-rgb), …)`
// (the comma-channelled token that Tailwind's `brand-gold` utilities cannot
// consume), and gold-edged selected states.

import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useMutation } from "@tanstack/react-query";
import { startRegistration } from "@simplewebauthn/browser";
import type { LucideIcon } from "lucide-react";
import {
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  Banknote,
  Building2,
  CheckCircle2,
  Compass,
  DollarSign,
  ExternalLink,
  FileSignature,
  FileText,
  Fingerprint,
  FolderLock,
  Handshake,
  Loader2,
  Lock,
  MessageSquare,
  Phone,
  Route,
  ScrollText,
  Shield,
  Users,
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { usePartnerAgent } from "@/components/investor-layout";
import { isDeviceHandled, markDeviceHandled } from "@/components/passkey-setup-prompt";
import {
  isPasskeyAlreadyOnDeviceError,
  isPasskeyCancelledError,
  rethrowPasskeyRegistrationError,
} from "@/lib/passkey-errors";

const SUPPORT_EMAIL = "support@eb5veras.com";

export type WelcomeStepKey = "welcome" | "path" | "contact" | "terms" | "passkey";

const STEP_LABELS: Record<WelcomeStepKey, string> = {
  welcome: "Welcome",
  path: "Your path",
  contact: "Reach us",
  terms: "Terms",
  passkey: "Passkey",
};

const GOLD = "rgb(var(--brand-gold-rgb))";
const GOLD_SOFT = "rgba(var(--brand-gold-rgb), 0.45)";

/** Five steps shown on the Overview progress rail, in the same order and wording. */
const JOURNEY_STEPS: Array<{ icon: LucideIcon; label: string; hint: string }> = [
  { icon: Shield, label: "Confirm eligibility", hint: "A short confirmation" },
  { icon: FolderLock, label: "Enter your deal room", hint: "Sign the NDA, review documents" },
  { icon: DollarSign, label: "Commit your investment", hint: "Reserve your place" },
  { icon: FileSignature, label: "Sign your agreement", hint: "E-sign in minutes" },
  { icon: Banknote, label: "Wire your funds", hint: "We confirm receipt" },
];

function isWebAuthnSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof window.navigator?.credentials?.create === "function"
  );
}

async function registerPasskeyOnThisDevice(): Promise<void> {
  try {
    const startRes = await apiRequest("POST", "/api/auth/passkey/register/start");
    const options = await startRes.json();
    const challengeId: string | undefined = options._challengeId;
    const attResp = await startRegistration({ optionsJSON: options });
    const finishRes = await apiRequest("POST", "/api/auth/passkey/register/finish", {
      ...attResp,
      _challengeId: challengeId,
      label: "My passkey",
    });
    if (!finishRes.ok) {
      const body = await finishRes.json().catch(() => ({}));
      throw new Error((body as { error?: string }).error || "Registration failed");
    }
  } catch (err) {
    rethrowPasskeyRegistrationError(err);
  }
}

/* ── Small branded building blocks ─────────────────────────────────────── */

function IconChip({ icon: Icon, className }: { icon: LucideIcon; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-inset ring-primary/20",
        className,
      )}
    >
      <Icon className="h-4 w-4" />
    </span>
  );
}

function FactRow({
  icon,
  title,
  text,
  accent = false,
  testId,
}: {
  icon: LucideIcon;
  title: string;
  text?: string;
  accent?: boolean;
  testId?: string;
}) {
  return (
    <li
      className={cn(
        "relative flex items-center gap-3 overflow-hidden rounded-xl border bg-white px-3.5 py-2.5",
        accent ? "border-primary/40 shadow-sm" : "border-gray-200",
      )}
      data-testid={testId}
    >
      {accent && (
        <span
          aria-hidden="true"
          className="absolute inset-y-0 left-0 w-1"
          style={{ backgroundColor: GOLD }}
        />
      )}
      <IconChip icon={icon} />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900 leading-snug">{title}</p>
        {text && <p className="text-xs text-gray-500 leading-snug mt-0.5">{text}</p>}
      </div>
    </li>
  );
}

function StepHeader({
  icon,
  eyebrow,
  title,
  subtitle,
  titleRef,
}: {
  icon: LucideIcon;
  eyebrow?: string;
  title: string;
  subtitle: string;
  titleRef: RefObject<HTMLHeadingElement>;
}) {
  return (
    <div className="flex items-start gap-3">
      <IconChip icon={icon} className="mt-0.5 h-11 w-11 rounded-xl [&>svg]:h-5 [&>svg]:w-5" />
      <div className="min-w-0">
        {eyebrow && (
          <p
            className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary"
            data-testid="welcome-step-eyebrow"
          >
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: GOLD }} />
            {eyebrow}
          </p>
        )}
        <DialogTitle asChild>
          <h2
            ref={titleRef}
            tabIndex={-1}
            className="font-heading text-xl font-bold leading-tight text-primary outline-none"
            data-testid="welcome-step-title"
          >
            {title}
          </h2>
        </DialogTitle>
        <span
          aria-hidden="true"
          className="mt-1.5 block h-0.5 w-10 rounded-full"
          style={{ backgroundColor: GOLD }}
        />
        <DialogDescription className="mt-2 text-sm text-gray-600" data-testid="welcome-step-subtitle">
          {subtitle}
        </DialogDescription>
      </div>
    </div>
  );
}

/* ── Component ──────────────────────────────────────────────────────────── */

export interface InvestorWelcomeModalProps {
  open: boolean;
  /** Investor's first name, used in the greeting. */
  firstName?: string | null;
  /** True while /api/auth/me reports the current terms version is unaccepted. */
  needsTermsAcceptance: boolean;
  /** Called once the flow is finished or dismissed after all required steps. */
  onComplete: () => void;
}

type PasskeyState = "offer" | "registering" | "success" | "error";

export function InvestorWelcomeModal({
  open,
  firstName,
  needsTermsAcceptance,
  onComplete,
}: InvestorWelcomeModalProps) {
  const partnerAgent = usePartnerAgent();

  // Freeze the step list at mount so accepting terms mid-flow doesn't
  // collapse the wizard underneath the user.
  const [includeTerms] = useState(needsTermsAcceptance);
  const [passkeySupported] = useState(() => isWebAuthnSupported());
  const [includePasskey] = useState(() => !isDeviceHandled());

  const steps = useMemo<WelcomeStepKey[]>(() => {
    const list: WelcomeStepKey[] = ["welcome", "path", "contact"];
    if (includeTerms) list.push("terms");
    if (includePasskey) list.push("passkey");
    return list;
  }, [includeTerms, includePasskey]);

  const [stepIndex, setStepIndex] = useState(0);
  const step = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  const [termsChecked, setTermsChecked] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsFailures, setTermsFailures] = useState(0);
  const termsPending = includeTerms && !termsAccepted;
  const termsIsLast = steps[steps.length - 1] === "terms";

  const [passkeyState, setPasskeyState] = useState<PasskeyState>("offer");
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const registrationInFlight = useRef(false);

  const titleRef = useRef<HTMLHeadingElement>(null);
  const hasMounted = useRef(false);
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    titleRef.current?.focus();
  }, [stepIndex]);

  const acceptTerms = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/accept-terms", {});
    },
    onSuccess: () => {
      setTermsFailures(0);
      setTermsAccepted(true);
      queryClient.setQueryData(
        ["/api/auth/me"],
        (prev: Record<string, unknown> | null | undefined) =>
          prev ? { ...prev, needsTermsAcceptance: false } : prev,
      );
      if (termsIsLast) {
        finish();
      } else {
        advance();
      }
    },
    onError: () => {
      setTermsFailures((n) => n + 1);
    },
  });

  const registerPasskey = useMutation({
    mutationFn: registerPasskeyOnThisDevice,
    onSuccess: () => {
      markDeviceHandled();
      queryClient.invalidateQueries({ queryKey: ["/api/auth/passkey"] });
      setPasskeyState("success");
    },
    onError: (err: unknown) => {
      if (isPasskeyCancelledError(err)) {
        setPasskeyState("offer");
      } else if (isPasskeyAlreadyOnDeviceError(err)) {
        markDeviceHandled();
        setPasskeyState("success");
      } else {
        setPasskeyError("Something went wrong. You can add a passkey later in Account Settings.");
        setPasskeyState("error");
      }
    },
    onSettled: () => {
      registrationInFlight.current = false;
    },
  });

  // Closing the wizard — Escape, outside click, Skip intro when nothing is
  // left to decide, or "Go to my dashboard" — must not mark the passkey
  // device handled. PasskeySetupPrompt is unmounted while this wizard is
  // open, so a premature mark would swallow the offer for good.
  function finish() {
    onComplete();
  }

  function skipPasskey() {
    markDeviceHandled();
    onComplete();
  }

  function advance() {
    setStepIndex((i) => {
      if (i >= steps.length - 1) return i;
      return i + 1;
    });
  }

  function handleNext() {
    if (isLast) {
      finish();
      return;
    }
    advance();
  }

  function handleBack() {
    if (!isFirst) setStepIndex((i) => i - 1);
  }

  /** Jump past the intro to the first step that needs a decision. */
  function handleSkipIntro() {
    const target = steps.findIndex((s) => s === "terms" || s === "passkey");
    if (target === -1) {
      finish();
    } else {
      setStepIndex(target);
    }
  }

  function handleSetUpPasskey() {
    if (registrationInFlight.current || registerPasskey.isPending) return;
    registrationInFlight.current = true;
    setPasskeyError(null);
    setPasskeyState("registering");
    registerPasskey.mutate();
  }

  const locked = termsPending || passkeyState === "registering";
  const showSkipIntro = isFirst && (step === "welcome" || step === "path" || step === "contact");
  const greetingName = firstName?.trim() ? `, ${firstName.trim()}` : "";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next || locked) return;
        finish();
      }}
    >
      <DialogContent
        className={cn(
          "theme-investor flex flex-col gap-0 overflow-hidden border-primary/15 bg-white p-0 text-gray-900 shadow-xl",
          "top-auto bottom-0 h-auto max-h-[calc(100dvh-0.5rem)] w-full max-w-md rounded-t-2xl rounded-b-none",
          "sm:top-1/2 sm:bottom-auto sm:-translate-y-1/2 sm:rounded-2xl",
          // The footer always carries an explicit exit (Skip intro / Skip for
          // now / Go to my dashboard) and Escape still closes when unlocked,
          // so the corner X is dropped rather than colliding with the rail.
          "[&>button]:hidden",
        )}
        onInteractOutside={(e) => {
          if (locked) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (locked) e.preventDefault();
        }}
        data-testid="investor-welcome-modal"
        data-step={step}
      >
        <span
          aria-hidden="true"
          className="h-1 w-full shrink-0"
          style={{ backgroundImage: `linear-gradient(90deg, hsl(var(--primary)), hsl(var(--primary) / 0.7), ${GOLD})` }}
        />

        {/* Progress rail */}
        <div className="flex items-center gap-3 px-6 pt-5">
          <div
            className="flex flex-1 items-center gap-1.5"
            role="progressbar"
            aria-valuemin={1}
            aria-valuenow={stepIndex + 1}
            aria-valuemax={steps.length}
            aria-valuetext={`Step ${stepIndex + 1} of ${steps.length}: ${STEP_LABELS[step]}`}
            data-testid="welcome-progress"
          >
            {steps.map((s, i) => (
              <span
                key={s}
                title={STEP_LABELS[s]}
                className={cn(
                  "flex-1 rounded-full transition-all",
                  i < stepIndex ? "h-1.5 bg-primary" : i === stepIndex ? "h-2 shadow-sm" : "h-1.5 bg-primary/15",
                )}
                style={i === stepIndex ? { backgroundColor: GOLD } : undefined}
                data-testid={`welcome-progress-${s}`}
                data-state={i < stepIndex ? "complete" : i === stepIndex ? "current" : "upcoming"}
              />
            ))}
          </div>
          <p
            className="shrink-0 rounded-full border border-primary/20 bg-primary/5 px-2.5 py-0.5 text-[11px] font-semibold text-primary"
            data-testid="welcome-step-count"
          >
            {stepIndex + 1} / {steps.length}
          </p>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-2 pt-4">
          <div className="space-y-4">
            {step === "welcome" && (
              <>
                <StepHeader
                  icon={Compass}
                  eyebrow={partnerAgent ? `Referred by ${partnerAgent.companyName}` : undefined}
                  title={`Welcome to Veras${greetingName}`}
                  subtitle="Your EB-5 investment, guided from first look to funded."
                  titleRef={titleRef}
                />
                {partnerAgent?.logoUrl && (
                  <img
                    src={partnerAgent.logoUrl}
                    alt={`${partnerAgent.companyName} logo`}
                    className="h-7 w-auto max-w-[140px] object-contain"
                    data-testid="img-welcome-partner-logo"
                  />
                )}
                <ul className="space-y-2">
                  <FactRow icon={Building2} title="Vetted EB-5 projects" text="Curated opportunities, clear terms." />
                  <FactRow icon={FolderLock} title="Secure deal rooms" text="Confidential documents, e-signatures, wire details." />
                  <FactRow
                    icon={Users}
                    title={partnerAgent ? `${partnerAgent.companyName} + Veras` : "A team beside you"}
                    text={partnerAgent ? "Your partner leads; we run the platform." : "Real people, every step of the way."}
                  />
                </ul>
              </>
            )}

            {step === "path" && (
              <>
                <StepHeader
                  icon={Route}
                  title="Five steps to your investment"
                  subtitle="Your Overview tracks each one. Start with eligibility."
                  titleRef={titleRef}
                />
                <ol className="space-y-2" data-testid="welcome-journey-steps">
                  {JOURNEY_STEPS.map((j, i) => (
                    <li
                      key={j.label}
                      className={cn(
                        "relative flex items-center gap-3 overflow-hidden rounded-xl border bg-white px-3.5 py-2.5",
                        i === 0 ? "border-primary/40 shadow-sm" : "border-gray-200",
                      )}
                      data-testid={`welcome-journey-step-${i + 1}`}
                    >
                      {i === 0 && (
                        <span aria-hidden="true" className="absolute inset-y-0 left-0 w-1" style={{ backgroundColor: GOLD }} />
                      )}
                      <span
                        aria-hidden="true"
                        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground"
                        style={i === 0 ? { boxShadow: `0 0 0 2px ${GOLD_SOFT}` } : undefined}
                      >
                        {i + 1}
                      </span>
                      <IconChip icon={j.icon} className="h-8 w-8" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 leading-snug">{j.label}</p>
                        <p className="text-xs text-gray-500 leading-snug">{j.hint}</p>
                      </div>
                      {i === 0 && (
                        <span
                          className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary"
                          style={{ backgroundColor: "rgba(var(--brand-gold-rgb), 0.18)" }}
                        >
                          Start here
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
              </>
            )}

            {step === "contact" && (
              <>
                <StepHeader
                  icon={MessageSquare}
                  title="We're one message away"
                  subtitle="Questions at any step — just ask."
                  titleRef={titleRef}
                />
                <ul className="space-y-2">
                  {partnerAgent && (
                    <FactRow
                      icon={Handshake}
                      title={partnerAgent.companyName}
                      text="Your first point of contact for the investment."
                      accent
                      testId="welcome-contact-partner"
                    />
                  )}
                  <FactRow
                    icon={MessageSquare}
                    title="Inbox"
                    text="Message our team from the sidebar, any time."
                    accent={!partnerAgent}
                    testId="welcome-contact-inbox"
                  />
                  {!partnerAgent?.hideConsultationCtas && (
                    <FactRow
                      icon={Phone}
                      title="Consultations"
                      text="Book a call in your language and time zone."
                      testId="welcome-contact-consultations"
                    />
                  )}
                </ul>
              </>
            )}

            {step === "terms" && (
              <>
                <StepHeader
                  icon={ScrollText}
                  title="Terms & privacy"
                  subtitle="Two documents, one checkbox. Please review before continuing."
                  titleRef={titleRef}
                />
                <ul className="space-y-2">
                  {[
                    { href: "/terms", icon: FileText, label: "Terms of Service", testId: "link-welcome-terms" },
                    { href: "/privacy", icon: Lock, label: "Privacy Policy", testId: "link-welcome-privacy" },
                  ].map((doc) => (
                    <li key={doc.href}>
                      <a
                        href={doc.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:border-primary/40 hover:bg-primary/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        data-testid={doc.testId}
                      >
                        <IconChip icon={doc.icon} />
                        <span className="flex-1">{doc.label}</span>
                        <ExternalLink className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                        <span className="sr-only">(opens in a new tab)</span>
                      </a>
                    </li>
                  ))}
                </ul>
                <label
                  htmlFor="welcome-terms-checkbox"
                  className={cn(
                    "relative flex cursor-pointer items-start gap-3 overflow-hidden rounded-xl border-2 p-4 pl-5 transition-all focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
                    termsChecked
                      ? "border-primary/70 bg-primary/[0.06] shadow-sm"
                      : "border-gray-200 bg-white hover:border-primary/40 hover:bg-primary/[0.03]",
                  )}
                  data-testid="row-welcome-terms"
                >
                  <span
                    aria-hidden="true"
                    className="absolute inset-y-0 left-0 w-1 transition-colors"
                    style={{ backgroundColor: termsChecked ? GOLD : "transparent" }}
                  />
                  <Checkbox
                    id="welcome-terms-checkbox"
                    checked={termsChecked}
                    onCheckedChange={(v) => setTermsChecked(Boolean(v))}
                    disabled={acceptTerms.isPending || termsAccepted}
                    className="mt-0.5 h-[18px] w-[18px] border-gray-400 data-[state=checked]:border-primary data-[state=checked]:bg-primary"
                    data-testid="checkbox-welcome-terms"
                  />
                  <span className="text-sm leading-snug text-gray-800 select-none">
                    I have read and agree to the Terms of Service and Privacy Policy.
                    {termsAccepted && (
                      <span className="mt-1 block text-xs font-semibold text-primary" data-testid="text-welcome-terms-accepted">
                        Accepted — thank you.
                      </span>
                    )}
                  </span>
                  {termsChecked && (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  )}
                </label>
                {acceptTerms.isError && (
                  <div
                    role="alert"
                    className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                    data-testid="text-welcome-terms-error"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
                    <div className="space-y-1 leading-snug">
                      <p>We couldn't record your acceptance. Please try again.</p>
                      {termsFailures > 1 && (
                        <p data-testid="text-welcome-terms-support">
                          Still stuck? Email{" "}
                          <a
                            href={`mailto:${SUPPORT_EMAIL}`}
                            className="font-medium underline underline-offset-2"
                            data-testid="link-welcome-terms-support"
                          >
                            {SUPPORT_EMAIL}
                          </a>
                          .
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {step === "passkey" && (
              <>
                <StepHeader
                  icon={Fingerprint}
                  title={
                    passkeyState === "success"
                      ? "Passkey added"
                      : "Sign in with Face ID or fingerprint"
                  }
                  subtitle={
                    passkeyState === "success"
                      ? "Next time, one tap on this device signs you in."
                      : passkeySupported
                        ? "One tap next time on this device. Your password still works as a backup."
                        : "Passkeys aren't available in this browser."
                  }
                  titleRef={titleRef}
                />
                {passkeyState === "success" ? (
                  <div
                    className="flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 px-3.5 py-3"
                    data-testid="text-welcome-passkey-success"
                  >
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" aria-hidden="true" />
                    <p className="text-sm text-green-800">Passkey saved to this device.</p>
                  </div>
                ) : passkeyState === "error" ? (
                  <div
                    role="alert"
                    className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                    data-testid="text-welcome-passkey-error"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
                    <p>{passkeyError}</p>
                  </div>
                ) : !passkeySupported ? (
                  <div
                    className="flex items-start gap-2.5 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600"
                    data-testid="text-welcome-passkey-unsupported"
                  >
                    <Shield className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                    <p>Add one later from Account Settings on a device with Face ID, fingerprint, or a PIN.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Button
                      className="h-11 w-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
                      style={{ boxShadow: `0 0 0 1px ${GOLD_SOFT}` }}
                      onClick={handleSetUpPasskey}
                      disabled={registerPasskey.isPending}
                      data-testid="button-welcome-passkey-setup"
                    >
                      {registerPasskey.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                          Waiting for your device…
                        </>
                      ) : (
                        <>
                          <Fingerprint className="h-4 w-4" aria-hidden="true" />
                          Set up passkey
                        </>
                      )}
                    </Button>
                    <p className="text-center text-xs text-gray-500">
                      Add or remove passkeys any time in Account Settings.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
          {showSkipIntro ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-500 hover:text-gray-800"
              onClick={handleSkipIntro}
              data-testid="button-welcome-skip-intro"
            >
              Skip intro
            </Button>
          ) : !isFirst ? (
            <Button
              variant="outline"
              size="sm"
              className="border-gray-300 text-gray-700"
              onClick={handleBack}
              disabled={acceptTerms.isPending || passkeyState === "registering"}
              data-testid="button-welcome-back"
            >
              <ArrowLeft className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Back
            </Button>
          ) : (
            <span />
          )}

          {step === "terms" && !termsAccepted ? (
            <Button
              size="sm"
              className="bg-primary px-5 font-semibold text-primary-foreground hover:bg-primary/90"
              onClick={() => acceptTerms.mutate()}
              disabled={!termsChecked || acceptTerms.isPending}
              data-testid="button-welcome-accept-terms"
            >
              {acceptTerms.isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Saving…
                </>
              ) : acceptTerms.isError ? (
                "Try again"
              ) : (
                <>
                  Accept & continue <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
                </>
              )}
            </Button>
          ) : step === "passkey" && passkeyState !== "success" && passkeySupported && passkeyState !== "error" ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-600 hover:text-gray-900"
              onClick={skipPasskey}
              disabled={registerPasskey.isPending}
              data-testid="button-welcome-passkey-skip"
            >
              Skip for now
            </Button>
          ) : (
            <Button
              size="sm"
              className="bg-primary px-5 font-semibold text-primary-foreground hover:bg-primary/90"
              onClick={handleNext}
              data-testid="button-welcome-next"
            >
              {isLast ? (
                "Go to my dashboard"
              ) : (
                <>
                  Next <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
